import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import styles from './UserProfile.module.css';

type ProfileStatus = {
  hasMpdId: boolean | null;
  promoRegistered: boolean | null;
  publicId: string | null;
  messageCount: number | null;
  messagesEnabled: boolean | null;
  publicKeyPublished: boolean | null;
  publicKeyStatusError: string | null;
};

export default function UserProfile() {
  const { currentUser } = useAuth();
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);

  const userProfileServiceUrl =
    import.meta.env.VITE_USER_PROFILE_SERVICE_URL === 'RELATIVE'
      ? ''
      : import.meta.env.VITE_USER_PROFILE_SERVICE_URL || '';

  const { data: status, isLoading: loading, error: fetchError } = useQuery<ProfileStatus>({
    queryKey: ['userProfileStatus', currentUser?.uid, userProfileServiceUrl],
    enabled: Boolean(currentUser),
    queryFn: async () => {
      const uid = currentUser!.uid;
      const email = (currentUser!.email || '').trim();
      const token = await currentUser!.getIdToken().catch(() => null);
      if (!token) throw new Error('Unauthorized. Please sign in again.');
      const authHeaders = { Authorization: `Bearer ${token}` };

      const promoPromise = !email
        ? Promise.resolve(null)
        : fetch('/api/customers')
            .then(async r => {
              if (!r.ok) return null;
              const json = (await r.json()) as { customers?: Array<{ email?: string | null }> };
              const list = Array.isArray(json?.customers) ? json.customers : [];
              return list.some(c => (c?.email || '').toLowerCase() === email.toLowerCase());
            })
            .catch(() => null);

      const profilePromise = fetch(`${userProfileServiceUrl}/api/user-profiles/user/${encodeURIComponent(uid)}`, { headers: authHeaders })
        .then(async r => { if (!r.ok) return null; const json = (await r.json()) as { publicId?: string | null }; return (json?.publicId || '').trim() || null; })
        .catch(() => null);

      const messageCountPromise = fetch(`${userProfileServiceUrl}/api/user-profiles/me/message-count`, { headers: authHeaders })
        .then(async r => { if (!r.ok) return null; const json = (await r.json()) as { count?: number }; return typeof json?.count === 'number' ? json.count : null; })
        .catch(() => null);

      const messagesEnabledPromise = fetch(`${userProfileServiceUrl}/api/user-profiles/me/messages-enabled`, { headers: authHeaders })
        .then(async r => { if (!r.ok) return null; const json = (await r.json()) as { enabled?: boolean }; return typeof json?.enabled === 'boolean' ? json.enabled : null; })
        .catch(() => null);

      const canReceivePromise = fetch(`${userProfileServiceUrl}/api/user-profiles/me/can-receive`, { headers: authHeaders })
        .then(async r => { if (!r.ok) return null; const json = (await r.json()) as { hasKey?: boolean; publicId?: string | null }; return { hasKey: Boolean(json?.hasKey), publicId: (json?.publicId || '').trim() || null }; })
        .catch(() => null);

      const [promo, pid, msgCount, msgsEnabled, canReceive] = await Promise.all([
        promoPromise, profilePromise, messageCountPromise, messagesEnabledPromise, canReceivePromise,
      ]);

      return {
        hasMpdId: pid != null,
        promoRegistered: promo,
        publicId: pid,
        messageCount: msgCount,
        messagesEnabled: msgsEnabled,
        publicKeyPublished: canReceive ? canReceive.hasKey : null,
        publicKeyStatusError: canReceive ? null : 'Unable to check public key status.',
      };
    },
  });

  const error = fetchError ? (fetchError as Error).message || 'Failed to load profile status' : null;
  const hasMpdId = status?.hasMpdId ?? null;
  const promoRegistered = status?.promoRegistered ?? null;
  const publicId = status?.publicId ?? null;
  const messageCount = status?.messageCount ?? null;
  const messagesEnabled = status?.messagesEnabled ?? null;
  const publicKeyPublished = status?.publicKeyPublished ?? null;
  const publicKeyStatusError = status?.publicKeyStatusError ?? null;

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading user profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Profile Not Found</h2>
          <p>{error}</p>
          <p>The user profile you're looking for doesn't exist or couldn't be loaded.</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>No Profile Data</h2>
          <p>Please log in to view your profile.</p>
        </div>
      </div>
    );
  }

  const copyText = async (text: string, successMessage: string) => {
    setCopySuccess(null);
    setCopyError(null);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        ta.style.top = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) throw new Error('Copy failed');
      }
      setCopySuccess(successMessage);
    } catch (err) {
      console.error('Copy failed:', err);
      setCopyError('Copy failed');
    }
  };

  const publicProfileUrl = publicId
    ? `${window.location.origin}/profile/${encodeURIComponent(publicId)}`
    : null;

  return (
    <div className={styles.container}>
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className={styles.profileHeader}>
        <div className={styles.profileImageContainer}>
          <div className={styles.profileImagePlaceholder}>
            {(currentUser.email || 'A').slice(0, 1).toUpperCase()}
          </div>
        </div>
        <div className={styles.profileInfo}>
          <h1 className={styles.userName}>Profile</h1>
          <p className={styles.userEmail}>{currentUser.email || '-'}</p>
        </div>
      </div>

      {/* ── Panel grid ──────────────────────────────────────────── */}
      <div className={styles.panelGrid}>

        {/* Account panel */}
        <div className={styles.panel}>
          <div className={styles.panelIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <h3 className={styles.panelTitle}>Account</h3>
          <div className={styles.panelValue}>{currentUser.email || '-'}</div>
          <div className={styles.panelHint}>Firebase UID: {currentUser.uid.slice(0, 8)}…</div>
        </div>

        {/* MPD ID panel */}
        <div className={styles.panel}>
          <div className={styles.panelIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h3 className={styles.panelTitle}>MPD ID</h3>
          <div className={`${styles.panelValue} ${hasMpdId ? styles.panelValueGreen : ''}`}>
            {hasMpdId === null ? '—' : hasMpdId ? 'Active' : 'Not Created'}
          </div>
          {hasMpdId === true && publicProfileUrl && (
            <a
              className={styles.panelLink}
              href={publicProfileUrl}
              target="_blank"
              rel="noreferrer"
            >
              View my MPD ID
            </a>
          )}
        </div>

        {/* Messages panel */}
        <div className={styles.panel}>
          <div className={styles.panelIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <h3 className={styles.panelTitle}>Messages</h3>
          <div className={`${styles.panelNumber} ${messageCount != null && messageCount > 0 ? styles.panelNumberHighlight : ''}`}>
            {messagesEnabled === null ? '—' : 
             messagesEnabled === false ? 'Pending' : 
             messageCount === null ? '—' : messageCount}
          </div>
          <div className={styles.panelHint}>
            {messagesEnabled === null ? 'Loading…' : 
             messagesEnabled === false ? 'Publish your public key to enable messages' :
             messageCount === null ? 'Loading…' : 
             messageCount === 1 ? 'message waiting' : 'messages waiting'}
          </div>
        </div>

        {/* Promotions panel */}
        <div className={styles.panel}>
          <div className={styles.panelIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 12 20 22 4 22 4 12" />
              <rect x="2" y="7" width="20" height="5" />
              <line x1="12" y1="22" x2="12" y2="7" />
              <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
              <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
            </svg>
          </div>
          <h3 className={styles.panelTitle}>Promotions</h3>
          <div className={`${styles.panelValue} ${promoRegistered ? styles.panelValueGreen : ''}`}>
            {promoRegistered === null ? '—' : promoRegistered ? 'Registered' : 'Not Registered'}
          </div>
          <div className={styles.panelHint}>Email promotion status</div>
        </div>
      </div>

      {/* ── Public Profile panel (full width) ───────────────────── */}
      <div className={styles.panelWide}>
        <div className={styles.panelWideHeader}>
          <div className={styles.panelIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
          </div>
          <h3 className={styles.panelTitle}>Public Profile</h3>
        </div>

        {publicId ? (
          <>
            <div className={styles.publicIdRow}>
              <span className={styles.publicIdLabel}>Public ID</span>
              <code className={styles.publicIdValue}>{publicId}</code>
            </div>

            <div className={styles.publicIdRow}>
              <span className={styles.publicIdLabel}>Public Key</span>
              {publicKeyStatusError ? (
                <span className={styles.statusValueMuted}>{publicKeyStatusError}</span>
              ) : publicKeyPublished === null ? (
                <span className={styles.statusValueMuted}>Checking…</span>
              ) : publicKeyPublished ? (
                <span className={styles.statusValueGreen}>Published</span>
              ) : (
                <span className={styles.statusValueMuted}>Not Published</span>
              )}
            </div>

            {publicProfileUrl && (
              <div className={styles.socialLinks}>
                <a className={styles.socialLink} href={publicProfileUrl} target="_blank" rel="noreferrer">
                  Open Public Profile
                </a>
                <button
                  type="button"
                  className={styles.socialLink}
                  onClick={() => copyText(publicProfileUrl, 'Profile link copied')}
                >
                  Copy Link
                </button>
                <button
                  type="button"
                  className={styles.socialLink}
                  onClick={() => publicId && copyText(publicId, 'Public ID copied')}
                >
                  Copy Public ID
                </button>
              </div>
            )}
          </>
        ) : (
          <div className={styles.panelHint}>No Public ID found for this account yet.</div>
        )}

        {(copySuccess || copyError) && (
          <div
            className={styles.panelHint}
            style={{ marginTop: 12, color: copyError ? 'var(--error-color, #ff6b6b)' : 'var(--accent-color)' }}
          >
            {copyError || copySuccess}
          </div>
        )}
      </div>
    </div>
  );
}
